import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useSettings } from "@/contexts/SettingsContext";
import { toast } from "sonner";
import { SETTINGS_MODAL, SETTINGS_MODAL_GST } from "@/constants/testIds";

export function SettingsModal({ onClose }) {
  const { settings, updateSettings } = useSettings();
  const [draft, setDraft] = useState(settings);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const save = () => {
    updateSettings(draft);
    toast.success("Settings saved", { description: "Changes apply across all open tabs" });
    onClose?.();
  };

  const field = (key) => ({
    value: draft[key] ?? "",
    onChange: (e) => setDraft({ ...draft, [key]: e.target.value }),
  });

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center anim-fade-in"
      style={{ background: "rgba(28,25,23,0.32)", backdropFilter: "blur(12px)" }}
      data-testid={SETTINGS_MODAL.root}
    >
      <div className="glass-elevated rounded-[24px] p-7 w-[min(560px,calc(100vw-32px))] max-h-[88vh] overflow-auto anim-slide-up-lg">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="overline mb-1">Salon Settings</div>
            <h2 className="font-display text-[26px] font-bold text-[var(--color-text-primary)]">Personalise GLOSS</h2>
            <p className="font-inter text-[13px] text-[var(--color-text-muted)] mt-1">Changes sync to every open tab including the TV display.</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full glass-subtle flex items-center justify-center">
            <X size={16} />
          </button>
        </div>

        <div className="space-y-4">
          <Row label="Salon name">
            <input className={inputCls} {...field("salon_name")} data-testid={SETTINGS_MODAL.salonNameInput} />
          </Row>
          <Row label="Owner name">
            <input className={inputCls} {...field("owner_name")} />
          </Row>
          <div className="grid grid-cols-2 gap-3">
            <Row label="Branch name">
              <input className={inputCls} {...field("branch_name")} data-testid={SETTINGS_MODAL.branchNameInput} />
            </Row>
            <Row label="Currency">
              <input className={inputCls} {...field("currency")} />
            </Row>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Row label="Landline">
              <input className={inputCls} {...field("salon_landline")} />
            </Row>
            <Row label="WhatsApp">
              <input className={inputCls} {...field("salon_whatsapp")} />
            </Row>
          </div>
          <Row label={`Loyalty rate (1 point = ${draft.currency}${draft.loyalty_rate})`}>
            <input
              type="number" step="1" min="1"
              className={inputCls}
              value={draft.loyalty_rate}
              onChange={(e) => setDraft({ ...draft, loyalty_rate: Number(e.target.value) || 1 })}
            />
          </Row>
          <Row label={`Default cleaning buffer (${draft.buffer_minutes_default} min)`}>
            <input
              type="number" step="5" min="0"
              className={inputCls}
              value={draft.buffer_minutes_default}
              onChange={(e) => setDraft({ ...draft, buffer_minutes_default: Number(e.target.value) || 0 })}
            />
          </Row>
          <div className="flex items-center gap-4 flex-wrap">
            <Toggle label="Tips enabled" value={draft.tip_enabled} onChange={(v) => setDraft({ ...draft, tip_enabled: v })} />
            <Toggle label="Split billing" value={draft.split_billing_enabled} onChange={(v) => setDraft({ ...draft, split_billing_enabled: v })} />
            <Toggle label="Auto-promote waitlist" value={draft.auto_promote_waitlist} onChange={(v) => setDraft({ ...draft, auto_promote_waitlist: v })} />
            <Toggle label="Display promos" value={draft.display_promo_enabled !== false} onChange={(v) => setDraft({ ...draft, display_promo_enabled: v })} />
          </div>

          <div className="rounded-2xl p-4 mt-2" style={{ background: "rgba(212,168,83,0.08)", border: "1px solid rgba(212,168,83,0.30)" }}>
            <div className="overline mb-2" style={{ color: "#8E6A1A" }}>Tax & invoice</div>
            <div className="space-y-3">
              <Row label="GSTIN">
                <input className={inputCls} {...field("gstin")} data-testid={SETTINGS_MODAL_GST.gstinInput} placeholder="27AABCU9603R1ZX" />
              </Row>
              <div className="grid grid-cols-2 gap-3">
                <Row label="GST rate (%)">
                  <input
                    type="number" step="1" min="0" max="28"
                    className={inputCls}
                    value={draft.gst_rate}
                    onChange={(e) => setDraft({ ...draft, gst_rate: Number(e.target.value) || 0 })}
                    data-testid={SETTINGS_MODAL_GST.gstRateInput}
                  />
                </Row>
                <Row label="Invoice prefix">
                  <input className={inputCls} {...field("invoice_prefix")} data-testid={SETTINGS_MODAL_GST.invoicePrefixInput} placeholder="GLOSS" />
                </Row>
              </div>
              <Row label="Salon address (for invoice header)">
                <input className={inputCls} {...field("salon_address")} data-testid={SETTINGS_MODAL_GST.salonAddressInput} />
              </Row>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button className="btn-3d btn-glass-3d" onClick={onClose}>Cancel</button>
          <button className="btn-3d btn-gold-3d" onClick={save} data-testid={SETTINGS_MODAL.saveBtn}>Save changes</button>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full h-11 px-3 rounded-xl bg-white/70 border border-white/80 font-inter text-[14px] text-[var(--color-text-primary)] outline-none focus:border-[var(--color-gold-mid)] focus:ring-2 focus:ring-[var(--color-gold-mid)]/20 transition";

function Row({ label, children }) {
  return (
    <label className="block">
      <div className="font-inter text-[12.5px] font-medium text-[var(--color-text-secondary)] mb-1.5">{label}</div>
      {children}
    </label>
  );
}

function Toggle({ label, value, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`flex items-center gap-2 px-3 py-2 rounded-full text-[12.5px] font-inter font-medium border transition ${value ? "bg-[var(--color-mauve)]/10 border-[var(--color-mauve)]/40 text-[var(--color-mauve-deep)]" : "glass-subtle text-[var(--color-text-muted)]"}`}
    >
      <span className={`inline-block w-2.5 h-2.5 rounded-full ${value ? "bg-[var(--color-mauve)]" : "bg-[var(--color-text-faint)]"}`} />
      {label}
    </button>
  );
}

export default SettingsModal;
